import { IContext } from "@src/context";
import { vhmarea, vhmproperty, vhmtrainline } from "@src/proto/jsonapi";
import { EAreaType, messages } from "./index";
import { API_LANGUAGE_MAP } from "@src/utils/languageHelper";
import { ifSearchPopup } from "@src/utils/componentChecker";
import { jsonFetch } from "@src/utils/fetch";
import { getPageCategory } from "@src/utils/helper";

const HokurikuAndKoshinetsuRid = 405;
const BaseUrl = document.body.getAttribute("data-site-domain") || "";
const pageCategory = getPageCategory();
const countKey =
  pageCategory === "parking" ? "parkingSpotCount" : "propertyCount";

interface IRenderItem {
  count: number | null;
  url: string;
  type?: EAreaType;
  combineId?: string; //'aId-bId'
}
export interface IRCity extends vhmarea.ICity, IRenderItem {
  pName: string;
}
export interface IRPrefecture extends vhmarea.IPrefecture, IRenderItem {}
export interface IRRegion extends vhmarea.IRegion, IRenderItem {}

export interface IRStation extends vhmtrainline.IStation, IRenderItem {}
export interface IRLine extends vhmtrainline.ILine, IRenderItem {}

interface IRAreaData {
  regionList: IRRegion[];
  rIdPrefectureMap: PlainObject<IRPrefecture[]>;
  pIdCityMap: PlainObject<IRCity[]>;
  pIdLinesMap: PlainObject<IRLine[]>;
  pIdAndLIdStationsMap: PlainObject<IRStation[]>;
}
interface IIdRItemMap {
  region: PlainObject<IRenderItem>;
  prefecture: PlainObject<IRenderItem>;
  city: PlainObject<IRenderItem>;
  line: PlainObject<IRenderItem>;
  station: PlainObject<IRenderItem>;
}

class SearchDataHelper {
  private context: IContext | null;
  private areaResp: vhmarea.IArea;
  private trainLineResp: vhmtrainline.ITrainLine;
  private count: vhmproperty.IStats;
  private isConvertAreaJson: boolean;
  private convertAreaJsonCache: IRAreaData | null;
  private languagePath: string;
  private idRItemMap: IIdRItemMap | null;
  private jsonApiPromise: Promise<unknown> | null;

  constructor() {
    this.context = null;
    this.areaResp = {};
    this.trainLineResp = {};
    this.isConvertAreaJson = false;
    this.convertAreaJsonCache = null;
    this.languagePath = "/";
    this.count = {
      propertyCount: {
        region: {},
        prefecture: {},
        city: {},
        trainLine: {},
        trainStation: {},
      },
      parkingSpotCount: {
        region: {},
        prefecture: {},
        city: {},
        trainLine: {},
        trainStation: {},
      },
    };
    this.idRItemMap = null;
    this.jsonApiPromise = null;
  }

  init(context: IContext) {
    this.context = context;
    this.languagePath = API_LANGUAGE_MAP[context.locale];
    if (!ifSearchPopup()) return;
    this.jsonApiPromise = Promise.all([
      this.fetchAreaData(),
      this.fetchCount(),
      this.fetchMap(),
      this.fetchTrainLineData(),
    ]);
  }

  getAreaAndLineData() {
    this.convertAreaAndLineResp();
    return this.convertAreaJsonCache;
  }

  getJsonApiPromise() {
    return this.jsonApiPromise;
  }

  private fetchMap() {
    return fetch(
      `${BaseUrl}/static/images/area_map_${this.languagePath}.svg?d=1697091333158`
    )
      .then((res) => res.text())
      .then((svg) => {
        const mapEle = document.querySelector(".container-search-popup-map");
        if (!mapEle) return;
        mapEle.innerHTML = svg;
      })
      .catch((err) => {
        this.context?.errorNotifier.notify(err);
      });
  }

  private fetchAreaData() {
    return jsonFetch<vhmarea.Area>(
      `${BaseUrl}/api/area_${this.languagePath}.json`
    )
      .then((res) => {
        this.areaResp = res ?? {};
      })
      .catch((err) => {
        this.context?.errorNotifier.notify(err);
      });
  }

  private fetchTrainLineData() {
    return jsonFetch<vhmtrainline.ITrainLine>(
      `${BaseUrl}/api/trainline_${this.languagePath}.json`
    )
      .then((res) => {
        this.trainLineResp = res ?? {};
      })
      .catch((err) => {
        this.context?.errorNotifier.notify(err);
      });
  }

  private fetchCount() {
    return jsonFetch<vhmproperty.IStats>(`${BaseUrl}/api/property_stats.json`)
      .then((res) => {
        this.count = res ?? {};
      })
      .catch((err) => {
        this.context?.errorNotifier.notify(err);
      });
  }

  addCount(a: number | null, b: number | null) {
    if (a === null || b === null) return null;
    return a + b;
  }

  getCount(...keys: unknown[]) {
    const count = this.count[countKey];
    if (!count || keys.length <= 0) return null;
    let cur = count;
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i] as keyof vhmproperty.IStats[typeof countKey];
      cur = cur[key];
      if (cur === null || cur === undefined) return null;
    }
    return cur as number;
  }

  pureConvertAreaResp(areaResp: vhmarea.IArea) {
    const pIdCityMap: IRAreaData["pIdCityMap"] = {};
    const rIdPrefectureMap: IRAreaData["rIdPrefectureMap"] = {};
    const regionList: IRAreaData["regionList"] = [];

    const rIdRRegionMap: PlainObject<IRRegion> = {};
    const pIdRPrefectureMap: PlainObject<IRPrefecture> = {};
    const cIdRCityMap: PlainObject<IRCity> = {};

    const prefectureMap = new Map();
    const regionMap = new Map();

    /* area start */
    areaResp.regions?.forEach((item) => {
      if (typeof item.id !== "number") return;
      const count = this.getCount("region", item.id);
      // empty count don't show

      regionMap.set(item.id, item);
      const rItem = {
        ...item,
        count,
        type: EAreaType.region,
        url: `/${item.path}`,
      };
      regionList.push(rItem);
      rIdRRegionMap[item.id] = rItem;
    });

    areaResp.prefs?.forEach((item) => {
      if (typeof item.rid !== "number") return;
      if (typeof item.id !== "number") return;
      const prefName = this.convertName(item.name);
      prefectureMap.set(item.id, {
        ...item,
        name: prefName,
      });
      const count = this.getCount("prefecture", item.id);

      rIdPrefectureMap[item.rid] = rIdPrefectureMap[item.rid] || [];
      const rItem = {
        ...item,
        name: prefName,
        count,
        type: EAreaType.prefecture,
        url: `/${regionMap.get(prefectureMap.get(item.id).rid).path}/${
          item.path
        }`,
      };
      rIdPrefectureMap[item.rid].push(rItem);
      pIdRPrefectureMap[item.id] = rItem;
    });

    areaResp.cities?.forEach((item) => {
      if (typeof item.pid !== "number") return;
      if (typeof item.id !== "string") return;
      const count = this.getCount("city", item.id);

      pIdCityMap[item.pid] = pIdCityMap[item.pid] || [];
      const { path, name } = prefectureMap.get(item.pid);
      const cityItem = {
        ...item,
        count,
        pName: name,
        type: EAreaType.city,
        url: `/${regionMap.get(prefectureMap.get(item.pid).rid).path}/${path}/${
          item.path
        }`,
      };
      pIdCityMap[item.pid].push(cityItem);
      cIdRCityMap[item.id] = cityItem;
    });
    /* area end */

    return {
      pIdCityMap,
      rIdPrefectureMap,
      regionList,
      rIdRRegionMap,
      pIdRPrefectureMap,
      cIdRCityMap,
      prefectureMap,
      regionMap,
    };
  }

  getCityWithinParent(id: string, cIdRCityMap: PlainObject<IRCity>) {
    const pCid = cIdRCityMap[id]?.pCid;
    if (pCid && typeof pCid === "string") {
      return cIdRCityMap[pCid];
    }
    return cIdRCityMap[id];
  }

  convertAreaAndLineResp() {
    if (this.isConvertAreaJson) return;
    if (!this.areaResp || Object.keys(this.areaResp).length <= 0) return;
    if (!this.count[countKey]) return;
    this.isConvertAreaJson = true;

    const {
      pIdCityMap,
      rIdPrefectureMap,
      regionList,
      rIdRRegionMap,
      pIdRPrefectureMap,
      cIdRCityMap,
      prefectureMap,
      regionMap,
    } = this.pureConvertAreaResp(this.areaResp);

    const lIdRLineMap: PlainObject<IRLine> = {};
    const sIdRStationMap: PlainObject<IRStation> = {};

    /* trainLine start */
    const sIdStationMap: PlainObject<vhmtrainline.IStation> = {};
    this.trainLineResp.stations?.forEach((item) => {
      if (typeof item.id !== "number") return;
      sIdStationMap[item.id] = item;
    });

    const pIdLinesMap: IRAreaData["pIdLinesMap"] = {};
    const pIdAndLIdStationsMap: IRAreaData["pIdAndLIdStationsMap"] = {};

    this.trainLineResp.lines?.forEach((item) => {
      if (item.id === null || item.id === undefined) return;
      item.pids?.forEach((pid) => {
        pIdLinesMap[pid] = pIdLinesMap[pid] || [];
        const combineId = `${pid}-${item.id}`;
        pIdAndLIdStationsMap[combineId] = [];
        const count = this.getCount("trainLine", item.id, "count", pid);

        const rItem = {
          ...item,
          count,
          type: EAreaType.line,
          url: `/${regionMap.get(prefectureMap.get(pid).rid).path}/${
            prefectureMap.get(pid).path
          }/${item.path}`,
          combineId,
        };
        pIdLinesMap[pid].push(rItem);
        if (!item.id) return;
        lIdRLineMap[item.id] = rItem;
      });

      item.stas?.forEach((stationId) => {
        const station = sIdStationMap[stationId];
        const pid = station.pid;
        const combineId = `${pid}-${item.id}`;
        const count = this.getCount("trainStation", station.id);

        const rItem = {
          ...station,
          count,
          type: EAreaType.station,
          url: `/${regionMap.get(prefectureMap.get(pid).rid).path}/${
            prefectureMap.get(pid).path
          }/${station.path}`,
          combineId,
        };
        pIdAndLIdStationsMap[combineId].push(rItem);
        sIdRStationMap[stationId] = rItem;
      });
    });
    /* trainLine end */
    this.idRItemMap = {
      region: rIdRRegionMap,
      prefecture: pIdRPrefectureMap,
      city: cIdRCityMap,
      line: lIdRLineMap,
      station: sIdRStationMap,
    };
    // source data end

    /* Horuriku(5) binded with Koshinetsu(4)  >>  Hokuriku-Koshinetsu */
    const HokurikuAndKoshinetsu = {
      id: HokurikuAndKoshinetsuRid,
      name: `${regionList[4].name}-${regionList[3].name}`,
      count: this.addCount(regionList[4].count, regionList[3].count),
      type: EAreaType.region,
      url: "",
    };
    regionList.splice(3, 2, HokurikuAndKoshinetsu);

    this.convertAreaJsonCache = {
      regionList,
      rIdPrefectureMap,
      pIdCityMap,
      pIdAndLIdStationsMap,
      pIdLinesMap,
    };
    this.convertAreaJsonCache.rIdPrefectureMap[HokurikuAndKoshinetsuRid] =
      this.getHokurikuAndKoshinetsuListFromRIdPrefectureMap() as IRPrefecture[];
  }

  /* Horuriku(5) need binded with Koshinetsu(4)  >>  Hokuriku-Koshinetsu */
  getHokurikuAndKoshinetsuListFromRIdPrefectureMap() {
    const list = this.convertAreaJsonCache?.rIdPrefectureMap[4].concat(
      this.convertAreaJsonCache?.rIdPrefectureMap[5]
    );
    list?.sort((a, b) => {
      if (!a.name || !b.name) return -1;
      return a.name.localeCompare(b.name);
    });
    return list;
  }

  convertName(name: IRPrefecture["name"]) {
    if (!name) return name;
    const idxABNF = name.indexOf("-");
    if (idxABNF === -1) return name;
    return name.substring(0, idxABNF);
  }

  getRenderCityListByPid(pid: string) {
    // city here can be split by oArea and pCid
    // oArea === true >>> Other Districts
    // else  >>> pCid(city)
    if (!pid || !this.convertAreaJsonCache) return [];
    const otherName = this.context?.intl.formatMessage(messages.oArea);
    const cityList = this.convertAreaJsonCache.pIdCityMap[pid];
    const cIdCityMap: PlainObject<IRCity> = {};
    const otherCity: IRCity[] = [];
    const areaCity: { [pcId: string]: IRCity[] } = {};
    const pCityList: { groupName: string; groupId: string; list: IRCity[] }[] =
      [];
    for (let i = 0; i < cityList.length; i++) {
      const city = cityList[i];
      if (!city.id) continue;
      cIdCityMap[city.id] = city;
      if (city.oArea) {
        otherCity.push(city);
      }
      if (city.pCid) {
        areaCity[city.pCid] = areaCity[city.pCid] || [];
        areaCity[city.pCid].push(city);
      }
    }

    Object.entries(areaCity).forEach(([pcId, list]) => {
      pCityList.push({
        groupName: cIdCityMap[pcId].name ?? "",
        groupId: pcId,
        list,
      });
    });
    // same groupName merge into one group
    const mergePCityList: {
      groupName: string;
      groupId: string;
      list: IRCity[];
    }[] = [];

    pCityList.forEach((current) => {
      const { groupName, list } = current;
      const recordItem = mergePCityList.find(
        (item) => item.groupName === groupName
      );
      if (!recordItem) {
        mergePCityList.push(current);
        return;
      }
      recordItem.list = recordItem.list.concat(list);
    });
    mergePCityList.push({
      groupName: otherName ?? "",
      groupId: otherName ?? "",
      list: otherCity,
    });
    return mergePCityList;
  }

  getIdRenderItemMap() {
    return this.idRItemMap;
  }
}

const searchDataHelper = new SearchDataHelper();
export default searchDataHelper;
